import { z } from 'zod';
import { ulidSchema } from './id';
import { DISPLAY_NAME_MAX_LENGTH } from './user';

/**
 * Connected apps — B49, `BE-49.3` / `BE-49.4`.
 *
 * This file models the reviewable grant surface and the one-time credential
 * returned at consent. It deliberately does not define a generic delegated
 * proxy contract; external tool operations use the existing narrow route
 * schemas for the resources they touch.
 */

/** `connected_apps.scope` — `ck_connected_apps_scope` restated. */
export const connectedAppScopeSchema = z.enum(['read_only', 'read_write']);

export type ConnectedAppScope = z.infer<typeof connectedAppScopeSchema>;

const timestampStringSchema = z.string().min(1).max(64);

/** One row in `GET /me/connected-apps`, and the POST/DELETE response body. */
export const connectedAppGrantSchema = z.object({
  id: ulidSchema,
  toolName: z.string().min(1).max(255),
  scope: connectedAppScopeSchema,
  grantedAt: timestampStringSchema,
  expiresAt: timestampStringSchema.nullable(),
  lastActiveAt: timestampStringSchema.nullable(),
  revokedAt: timestampStringSchema.nullable(),
});

export type ConnectedAppGrant = z.infer<typeof connectedAppGrantSchema>;

export const connectedAppBearerTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{32,128}$/);

export const connectedAppConsentResponseSchema = connectedAppGrantSchema.extend({
  token: connectedAppBearerTokenSchema,
});

export type ConnectedAppConsentResponse = z.infer<
  typeof connectedAppConsentResponseSchema
>;

/** `GET /me/connected-apps`. */
export const connectedAppGrantListSchema = z.object({
  connectedApps: z.array(connectedAppGrantSchema),
});

export type ConnectedAppGrantList = z.infer<typeof connectedAppGrantListSchema>;

/** `POST /me/connected-apps` — the explicit consent receipt. */
export const connectedAppConsentBodySchema = z.strictObject({
  toolName: z.string().trim().min(1).max(255),
  scope: connectedAppScopeSchema,
  expiresAt: timestampStringSchema.nullable().optional(),
});

export type ConnectedAppConsentBody = z.infer<typeof connectedAppConsentBodySchema>;

/*
 * API tokens — `docs/notes/api-token-requirements.md` (AT-4, AT-6, AT-7, AT-8,
 * AT-11). An API token IS a connected-app grant plus its token row (brief D1),
 * so everything below extends the schemas above on COPIES: the six shipped
 * schemas stay byte-identical, because `connectedAppGrantSchema` still
 * serialises the POST and DELETE responses.
 */

/** Every token minted from AT-11 on starts with this (brief D12). */
export const API_TOKEN_PREFIX = 'molt_pat_';

/**
 * Member self-service issuance through `POST /me/connected-apps` (brief D3).
 *
 * One constant read by both server and web — deliberately not an environment
 * variable, so a developer's machine and production cannot differ.
 *
 * `false`: D3 was confirmed by the owner on 2026-09-14 — self-service is
 * closed and tokens exist only when a superadmin issued them (AT-6). While it
 * is `false`, `POST /me/connected-apps` stays registered and answers every
 * signed-in caller 403 `{ connectedApp: ['self_service_disabled'] }` before
 * the body or the database is touched, and the web hides the create form.
 * Grants members minted before the close stay valid, listed and revocable.
 *
 * Reopening is this one line flipped back to `true`, plus the reopen test in
 * `apps/server/src/routes/api-tokens.integration.test.ts`, which already
 * proves the route behaves as it did before the close.
 */
export const CONNECTED_APP_SELF_SERVICE_ISSUANCE = false;

/** AT-4 — the five expiry presets (brief D5). No custom date, no extension. */
export const apiTokenExpiryPresetSchema = z.enum(['7d', '30d', '90d', '1y', 'never']);

export type ApiTokenExpiryPreset = z.infer<typeof apiTokenExpiryPresetSchema>;

/** Days per preset; `null` stores `expires_at = NULL`, which never expires. 1 year = 365 days. */
export const API_TOKEN_EXPIRY_DAYS: Readonly<
  Record<ApiTokenExpiryPreset, number | null>
> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '1y': 365,
  never: null,
};

export const API_TOKEN_DEFAULT_EXPIRY: ApiTokenExpiryPreset = '90d';

/**
 * Derived, never stored: `revoked` if the grant is revoked; else `expired`;
 * else `unclaimed` if it holds no unrevoked token row; else `active`.
 */
export const connectedAppGrantStatusSchema = z.enum([
  'unclaimed',
  'active',
  'expired',
  'revoked',
]);

export type ConnectedAppGrantStatus = z.infer<typeof connectedAppGrantStatusSchema>;

/**
 * Who issued a grant for its member (`connected_apps.issued_by_user_id`).
 * Public display name only — no email. `max` without `min`, like
 * `publicProfileSchema`: reject on the way in, render what is there on the
 * way out.
 */
export const apiTokenIssuerSchema = z.object({
  id: ulidSchema,
  displayName: z.string().max(DISPLAY_NAME_MAX_LENGTH),
});

export type ApiTokenIssuer = z.infer<typeof apiTokenIssuerSchema>;

/**
 * AT-7 — one row of `GET /me/connected-apps`: the shipped grant plus three
 * REQUIRED fields. `tokenHint` is `connected_app_tokens.token_hint`
 * (`varchar(8)`), `null` for an unclaimed grant and for tokens minted before
 * the hint existed. `issuedBy` is `null` when the member issued it themselves.
 */
export const memberConnectedAppGrantSchema = connectedAppGrantSchema.extend({
  status: connectedAppGrantStatusSchema,
  tokenHint: z.string().max(8).nullable(),
  issuedBy: apiTokenIssuerSchema.nullable(),
});

export type MemberConnectedAppGrant = z.infer<typeof memberConnectedAppGrantSchema>;

/** AT-7 — `GET /me/connected-apps`, the member copy. */
export const memberConnectedAppGrantListSchema = z.object({
  connectedApps: z.array(memberConnectedAppGrantSchema),
});

export type MemberConnectedAppGrantList = z.infer<
  typeof memberConnectedAppGrantListSchema
>;

/**
 * AT-8 — `POST /me/connected-apps/:id/token` (201): the only response that
 * carries a cleartext token besides the self-service consent response, which
 * AT-6 has closed (`CONNECTED_APP_SELF_SERVICE_ISSUANCE`).
 */
export const connectedAppTokenRevealSchema = z.object({
  connectedApp: memberConnectedAppGrantSchema,
  token: connectedAppBearerTokenSchema,
});

export type ConnectedAppTokenReveal = z.infer<typeof connectedAppTokenRevealSchema>;
